import {
  createStyles,
  FormControlLabel,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { IVerifyAddressResponse } from 'api/file/interfaces/verify-address';
import { FileCreateContext, FileCreateContextInterface } from 'hooks/FileCreateContext';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import React, { useContext, useEffect, useState } from 'react';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    propertyCard: {
      margin: theme.spacing(1, 0),
    },
    propertyDetailLabel: {
      fontWeight: 'bold',
      marginRight: theme.spacing(1),
    },
    propertyDetailItem: {
      marginBottom: theme.spacing(1),
    },
    radioGroups: {
      width: '100%',
    },
    radioGroup: {
      width: '100%',
      marginBottom: theme.spacing(2),
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1),
      '&.MuiButtonBase-root': {
        paddingTop: theme.spacing(0),
      },
    },
    selectedProperty: {
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.action.selected,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    instructions: {
      marginBottom: theme.spacing(3),
    },
    addressBlock: {
      whiteSpace: 'pre-line',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(1),
    },
    tableContainer: {
      marginTop: theme.spacing(2),
    },
    tableHeader: {
      backgroundColor: theme.palette.background.paper,
    },
    tableHeaderCell: {
      fontWeight: 'bold',
      height: '40px !important',
    },
    verticalDivider: {
      position: 'relative',
      paddingLeft: theme.spacing(0.5),
      '&::after': {
        content: '""',
        position: 'absolute',
        top: theme.spacing(1),
        bottom: theme.spacing(1),
        right: 0,
        width: '1px',
        backgroundColor: theme.palette.grey[300],
      },
    },
    successAlert: {
      height: 20,
      padding: '0px 6px',
      alignItems: 'center',
      borderRadius: 100,
      background: '#E8F5E9',
      gap: 4,
      color: theme.palette.success.dark,
      width: 'fit-content',
      float: 'right',
      marginRight: theme.spacing(3),
      display: 'flex',
    },
    radioCell: {
      width: '48px',
      padding: theme.spacing(1),
    },
    addressCell: {
      padding: theme.spacing(1, 0.5),
      width: '60%',
    },
    apnCell: {
      padding: theme.spacing(1, 2),
    },
    tableRow: {
      '&:hover': {
        backgroundColor: theme.palette.action.selected,
      },
    },
    selectedRow: {
      backgroundColor: theme.palette.primary.light,
    },
    cardTitle: {
      marginBottom: theme.spacing(1),
    },
  }),
);

export interface VerifiedPropertiesDrawerProps {
  propertiesByIndex: Record<number, IVerifyAddressResponse[]>;
  sourcePage: string;
  submitAction?: () => void;
}

const VerifiedPropertiesDrawer: React.FC<VerifiedPropertiesDrawerProps> = ({
  propertiesByIndex,
  sourcePage,
  submitAction,
}) => {
  const classes = useStyles();
  const { addSnackbarMessage } = useSnackBars();

  // Get file data context
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const fileCreateCtx: FileCreateContextInterface = useContext(FileCreateContext) ?? {};
  const { fileCreateData, setFileCreateData } = fileCreateCtx;
  const { fileData, setFileData } = fileDataCtx;

  // Convert dictionary to array of entries for processing
  const propertyEntries = Object.entries(propertiesByIndex).map(([index, properties]) => ({
    index: Number(index),
    properties,
  }));

  const [currentEntryIndex, setCurrentEntryIndex] = useState(0);
  const [selectedProperty, setSelectedProperty] = useState<IVerifyAddressResponse | null>(null);
  const [openVerifyDrawer, setOpenVerifyDrawer] = useState(true);
  const [isVerifySubmitted, setIsVerifySubmitted] = useState(false);

  // Get current property entry
  const currentEntry = propertyEntries[currentEntryIndex];
  const currentProperties = currentEntry?.properties || [];
  const currentPropertyIndex = currentEntry?.index || 0;
  const isLastProperty = currentEntryIndex === Object.keys(propertiesByIndex).length - 1;

  const handlePropertySelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const propertyId = Number(event.target.value);
    const selected = currentProperties.find(
      (property: IVerifyAddressResponse) => property.propertyDetailId === propertyId,
    );
    if (selected) {
      setSelectedProperty(selected);
    }
  };

  const handleSubmit = () => {
    if (!selectedProperty) {
      addSnackbarMessage({
        html: true,
        message: `<b class="savingFailedErrorHeading">Select a validated address to use.</b>`,
        type: 'error',
      });
      return;
    }

    setIsVerifySubmitted(true);

    // Update file data for current property
    if (fileData?.properties && setFileData && sourcePage != 'create') {
      const updatedProperties = [...fileData.properties];
      const property = updatedProperties[currentPropertyIndex];

      if (property) {
        // Update property with validated data
        property.propertyDetailId =
          selectedProperty.propertyDetailId === 0 && currentProperties.length === 2
            ? currentProperties[1].propertyDetailId
            : selectedProperty.propertyDetailId;
        property.address1 = selectedProperty.address1;
        property.address2 = selectedProperty.address2;
        property.city = selectedProperty.city;
        property.state = selectedProperty.stateCode;
        property.postalCode = selectedProperty.zip;
        property.county = selectedProperty.county;
        property.apn =
          !selectedProperty.apn && currentProperties.length === 2 ? currentProperties[1].apn : selectedProperty.apn;

        setFileData({ ...fileData, properties: updatedProperties });
      }
    } else if (fileCreateData?.properties && setFileCreateData && sourcePage == 'create') {
      const updatedProperties = [...(fileCreateData.properties ?? [])];
      const property = updatedProperties[currentPropertyIndex];
      if (property) {
        // Update property with validated data
        property.propertyDetailId =
          selectedProperty.propertyDetailId === 0 && currentProperties.length === 2
            ? currentProperties[1].propertyDetailId
            : selectedProperty.propertyDetailId;
        property.address1 = selectedProperty.address1;
        property.address2 = selectedProperty.address2;
        property.city = selectedProperty.city;
        property.state = selectedProperty.stateCode;
        property.postalCode = selectedProperty.zip;
        property.county = selectedProperty.county;
        property.apn =
          !selectedProperty.apn && currentProperties.length === 2 ? currentProperties[1].apn : selectedProperty.apn;

        setFileCreateData({ ...fileCreateData, properties: updatedProperties });
      }
    }

    // Move to next property or finish
    if (isLastProperty) {
      // This is the last property, call the final submit action
      submitAction && submitAction();
    } else {
      // Move to next property
      setCurrentEntryIndex((prev) => prev + 1);
      setSelectedProperty(null);
      setIsVerifySubmitted(false);
    }
  };

  useEffect(() => {
    if (propertyEntries.length > 0 && currentProperties.length > 0) {
      setSelectedProperty(currentProperties[0]);
      setOpenVerifyDrawer(true);
    }
  }, [currentEntryIndex, currentProperties]);

  // Helper function to format address for display in a single line
  const formatAddress = (property: IVerifyAddressResponse): string => {
    const parts = [property.address1, property.address2, property.city, `${property.stateCode} ${property.zip}`].filter(
      (part) => part && part.trim() !== '',
    );
    return parts.join(', ');
  };

  // Helper function to format address in multi-line format
  const formatAddressMultiline = (property: IVerifyAddressResponse): string => {
    const parts = [
      property.address1,
      property.address2,
      `${property.city}, ${property.stateCode} ${property.zip}`,
      `County: ${property.county}`,
    ].filter((part) => part && part.trim() !== '');

    if (property.apn) {
      parts.push('', `APN: ${property.apn}`);
    }

    return parts.join('\n');
  };

  // Render property cards for exactly 2 addresses
  const renderTwoAddressesView = () => {
    return (
      <RadioGroup
        value={selectedProperty?.propertyDetailId}
        onChange={handlePropertySelect}
        className={classes.radioGroups}
      >
        {currentProperties.map((property: IVerifyAddressResponse, index: number) => (
          <FormControlLabel
            key={property.propertyDetailId}
            value={property.propertyDetailId}
            control={<Radio color="primary" data-testid={`VerifyAddressOption${index + 1}`} />}
            label={
              <div className={classes.propertyCard}>
                <Typography variant="h5" className={classes.cardTitle}>
                  {index === 0 ? 'Address Entered' : 'Validated Address'}
                </Typography>
                <Typography variant="body2" className={classes.addressBlock}>
                  {formatAddressMultiline(property)}
                </Typography>
              </div>
            }
            className={`${classes.radioGroup} ${
              selectedProperty?.propertyDetailId === property.propertyDetailId ? classes.selectedProperty : ''
            }`}
          />
        ))}
      </RadioGroup>
    );
  };

  // Render table view for more than 2 addresses
  const renderTableView = () => {
    return (
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table aria-label="verified properties table">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell className={classes.tableHeaderCell}></TableCell>
              <TableCell className={`${classes.tableHeaderCell} ${classes.verticalDivider}`}>Address</TableCell>
              <TableCell className={classes.tableHeaderCell}>APN</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentProperties.map((property: IVerifyAddressResponse, index: number) => (
              <TableRow
                key={property.propertyDetailId}
                className={`${classes.tableRow} ${
                  selectedProperty?.propertyDetailId === property.propertyDetailId ? classes.selectedRow : ''
                }`}
              >
                <TableCell className={classes.radioCell}>
                  <Radio
                    checked={selectedProperty?.propertyDetailId === property.propertyDetailId}
                    onChange={handlePropertySelect}
                    value={property.propertyDetailId}
                    name="property-radio-button"
                    color="primary"
                    data-testid={`VerifyAddressOption${index + 1}`}
                  />
                </TableCell>
                <TableCell className={classes.addressCell}>
                  {formatAddress(property)}
                  {index === 0 && (
                    <Typography variant="caption" className={classes.successAlert}>
                      Address Entered
                    </Typography>
                  )}
                </TableCell>
                <TableCell className={classes.apnCell}>
                  {property.apn}
                  {!property.apn && (
                    <Typography variant="caption" color="textSecondary">
                      Not Available
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Don't render if no properties
  if (propertyEntries.length === 0 || !currentEntry) {
    return null;
  }

  return (
    <div className={classes.root}>
      <FormDrawerComponent
        title={`Confirm Address`}
        open={openVerifyDrawer}
        primaryActionProps={{
          loading: isVerifySubmitted,
          disabled: isVerifySubmitted,
          'data-testid': 'VerifyAddressSubmit',
          size: 'small',
        }}
        dismissActionProps={{
          disabled: isVerifySubmitted,
          'data-testid': 'VerifyAddressCancel',
          size: 'small',
        }}
        crossQAPrefix="VerifyAddress"
        primaryActionLabel={
          Object.keys(propertiesByIndex).length > 1
            ? currentEntryIndex < Object.keys(propertiesByIndex).length - 1
              ? 'Select & Next'
              : 'Select & Save'
            : 'Use Selected Address'
        }
        onPrimaryAction={handleSubmit}
        onDismissAction={() => {
          setOpenVerifyDrawer(false);
        }}
        disabled={isVerifySubmitted}
        testId="scrollable-form-drawer"
        width={960}
      >
        <Typography variant="body1" className={classes.instructions}>
          <>
            We found {currentProperties.length === 2 ? 'a variation' : 'several variations'} of the property address you
            entered. Please review and select which address you&apos;d like to use for this transaction.
          </>
        </Typography>

        {currentProperties.length === 2 ? renderTwoAddressesView() : renderTableView()}
      </FormDrawerComponent>
    </div>
  );
};

export default VerifiedPropertiesDrawer;
